import React, { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { Box, ClickAwayListener, IconButton, Paper, Popper } from 'libs/shared/ui';

import { keyframes, styled } from '@mui/material';

import {
  buildSelectedTags,
  decodeTagArray,
  encodeTagArray,
  QUERY_PARAM_EXCLUDE_TAGS,
  QUERY_PARAM_INCLUDE_TAGS,
  QUERY_PARAM_SEARCH
} from 'Courses/hooks/urlSyncUtils';
import { Colors } from 'Themes/colors';

import { Spacing } from '@themes/spacing';

import { SearchInput, SearchTag, TagOptions } from './components';
import type { CourseTagWithTranslation, TagTypeType } from './types';
import { TagType as TagTypeConst } from './types';

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SearchContainer = styled(Box)`
  position: relative;
  width: 100%;
  max-width: 1200px;
  min-width: 600px;
`;

const SearchWrapper = styled(Box)`
  border: 1px solid ${Colors.lightGrey};
  border-radius: 4px;
  background-color: #fff;
  padding: 12px 16px;
  cursor: text;
  min-height: 48px;
  display: flex;
  align-items: center;
  gap: 12px;

  &:focus-within {
    border-color: ${Colors.blue};
    outline: 2px solid ${Colors.blue}20;
  }

  &:hover {
    border-color: ${Colors.darkGrey};
  }
`;

const TagsContainer = styled(Box)`
  display: flex;
  flex-wrap: nowrap;
  gap: ${Spacing.xsmall};
  max-width: 60%;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: ${Colors.lightGrey} transparent;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${Colors.lightGrey};
    border-radius: 2px;
  }
`;

const Separator = styled(Box)`
  width: 1px;
  height: 24px;
  background-color: ${Colors.lightGrey};
  flex-shrink: 0;
`;

const SearchInputContainer = styled(Box)`
  flex: 1;
  min-width: 200px;
`;

const ClearButton = styled(IconButton)`
  padding: 4px;
  color: ${Colors.darkGrey};
  flex-shrink: 0;

  &:hover {
    color: ${Colors.red};
    background-color: ${Colors.lightGrey};
  }
`;

const SuggestionOverlay = styled(Paper)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1300;
  border: 1px solid ${Colors.lightGrey};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: ${slideDown} 0.15s ease-out;
  max-height: 200px;
  overflow-y: auto;
`;

const SuggestionItem = styled(Box)`
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid ${Colors.lightGrey};
  display: flex;
  align-items: center;
  gap: ${Spacing.xsmall};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${Colors.lightGrey};
  }

  &[data-selected='true'] {
    background-color: ${Colors.blue}10;
  }
`;

const TagAnimation = styled(Box)`
  animation: ${fadeInScale} 0.2s ease-out;
`;

interface Props {
  availableTags?: readonly CourseTagWithTranslation[];
  areTagsLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const SearchBar = ({
  availableTags = [],
  areTagsLoading = false,
  placeholder = 'Search trainings...',
  disabled = false
}: Readonly<Props>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const searchText = (searchParams.get(QUERY_PARAM_SEARCH) ?? '').trim();
  const rawSearchText = searchParams.get(QUERY_PARAM_SEARCH) ?? '';
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showTagOptions, setShowTagOptions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<CourseTagWithTranslation[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [ariaLiveMessage, setAriaLiveMessage] = useState('');
  const [inputValue, setInputValue] = useState(searchText);

  // Sync input value with URL search text when URL changes
  useEffect(() => {
    setInputValue(searchText);
  }, [searchText]);
  const includeTags = decodeTagArray(searchParams.get(QUERY_PARAM_INCLUDE_TAGS));
  const excludeTags = decodeTagArray(searchParams.get(QUERY_PARAM_EXCLUDE_TAGS));
  const selectedTags = buildSelectedTags(includeTags, excludeTags);

  const selectedTagNames = new Set(selectedTags.map(tag => tag.name));

  const availableTagsToShow = availableTags.filter(tag => !selectedTagNames.has(tag.value));

  const getTagTranslation = (tag: CourseTagWithTranslation) => t(tag.translationKey, tag.value);

  const updateUrlParams = (
    newSearchText: string,
    newIncludeTags: string[],
    newExcludeTags: string[]
  ) => {
    const params = new URLSearchParams();

    if (newSearchText.trim()) {
      params.set(QUERY_PARAM_SEARCH, newSearchText.trim());
    }

    if (newIncludeTags.length > 0) {
      params.set(QUERY_PARAM_INCLUDE_TAGS, encodeTagArray(newIncludeTags));
    }

    if (newExcludeTags.length > 0) {
      params.set(QUERY_PARAM_EXCLUDE_TAGS, encodeTagArray(newExcludeTags));
    }

    const newUrl = params.toString()
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;

    navigate(newUrl, { replace: true });
  };

  const findMatchingTags = (input: string) => {
    if (!/^[+-]/.test(input) || input.trim().length <= 1) {
      return [];
    }

    const tagPattern = /^[+-]([a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s]+)$/;
    const match = tagPattern.exec(input.trim());

    if (!match) return [];

    const [, tagPrefix] = match;
    return availableTagsToShow.filter(tag => {
      const tagValue = tag.value.toLowerCase();
      const translatedName = getTagTranslation(tag).toLowerCase();
      const searchPrefix = tagPrefix.toLowerCase();

      return tagValue.startsWith(searchPrefix) || translatedName.startsWith(searchPrefix);
    });
  };

  const announceTagSelection = (tag: CourseTagWithTranslation, type: TagTypeType) => {
    const message =
      type === TagTypeConst.Include
        ? t('coursesPage.tagAddedInclude', `Added ${getTagTranslation(tag)} to include filters`)
        : t('coursesPage.tagAddedExclude', `Added ${getTagTranslation(tag)} to exclude filters`);
    setAriaLiveMessage(message);
    globalThis.setTimeout(() => setAriaLiveMessage(''), 3000);
  };

  const handleSearchTextChange = (text: string) => {
    setInputValue(text); // Update input value immediately

    const matchingTags = findMatchingTags(text);

    if (matchingTags.length === 1 && /^[+-][a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s]+$/.test(text.trim())) {
      const isInclude = text.startsWith('+');
      const isExclude = text.startsWith('-');
      const tag = matchingTags[0];

      if (isInclude || isExclude) {
        handleTagAdd(tag, isInclude ? TagTypeConst.Include : TagTypeConst.Exclude, true);
        announceTagSelection(tag, isInclude ? TagTypeConst.Include : TagTypeConst.Exclude);
        setShowSuggestions(false);
        setInputValue(''); // Clear input after adding tag
        return;
      }
    } else if (matchingTags.length > 1) {
      setSuggestions(matchingTags);
      setShowSuggestions(true);
      setSelectedSuggestionIndex(0);
    } else if (/^[+-]/.exec(text)) {
      setShowSuggestions(false);
    }

    updateUrlParams(text, includeTags, excludeTags);
  };

  const handleTagAdd = (
    tag: CourseTagWithTranslation,
    type: TagTypeType,
    clearSearchText = false
  ) => {
    if (disabled) {
      return;
    }

    const tagExists = includeTags.includes(tag.value) || excludeTags.includes(tag.value);
    if (tagExists) {
      return;
    }

    const newIncludeTags =
      type === TagTypeConst.Include ? [...includeTags, tag.value] : includeTags;
    const newExcludeTags =
      type === TagTypeConst.Exclude ? [...excludeTags, tag.value] : excludeTags;

    updateUrlParams(clearSearchText ? '' : rawSearchText, newIncludeTags, newExcludeTags);

    if (clearSearchText) {
      setInputValue('');
    }

    inputRef.current?.focus();
  };

  const handleSuggestionSelect = (tag: CourseTagWithTranslation, type: TagTypeType) => {
    handleTagAdd(tag, type);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    inputRef.current?.focus();
  };

  const handleSuggestionKeyDown = (event: KeyboardEvent) => {
    if (!showSuggestions) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedSuggestionIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedSuggestionIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
          const tag = suggestions[selectedSuggestionIndex];
          const isInclude = searchText.startsWith('+');
          handleSuggestionSelect(tag, isInclude ? TagTypeConst.Include : TagTypeConst.Exclude);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const handleTagToggle = (tagName: string, currentType: TagTypeType) => {
    const newIncludeTags =
      currentType === TagTypeConst.Include
        ? includeTags.filter(tag => tag !== tagName)
        : [...includeTags, tagName];
    const newExcludeTags =
      currentType === TagTypeConst.Exclude
        ? excludeTags.filter(tag => tag !== tagName)
        : [...excludeTags, tagName];

    updateUrlParams(rawSearchText, newIncludeTags, newExcludeTags);
  };

  const handleTagRemove = (tagName: string) => {
    const newIncludeTags = includeTags.filter(tag => tag !== tagName);
    const newExcludeTags = excludeTags.filter(tag => tag !== tagName);

    updateUrlParams(rawSearchText, newIncludeTags, newExcludeTags);
  };

  const handleClearAll = () => {
    setInputValue('');
    navigate(location.pathname, { replace: true });
    inputRef.current?.focus();
  };

  const handleContainerClick = () => {
    if (disabled) {
      return;
    }

    inputRef.current?.focus();
    setAnchorEl(containerRef.current);
    setShowTagOptions(true);
  };

  const handleInputFocus = () => {
    if (disabled) {
      return;
    }

    setAnchorEl(containerRef.current);
    setShowTagOptions(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }

    if (showSuggestions) {
      handleSuggestionKeyDown(event);
      return;
    }

    if (event.key === 'Escape') {
      setShowTagOptions(false);
      setShowSuggestions(false);
      return;
    }

    if (event.key === 'ArrowDown' && !showTagOptions) {
      event.preventDefault();
      setAnchorEl(containerRef.current);
      setShowTagOptions(true);
      return;
    }

    const hasTags = includeTags.length > 0 || excludeTags.length > 0;
    if (event.key === 'Backspace' && !searchText && hasTags) {
      event.preventDefault();
      const lastTag = [...includeTags, ...excludeTags].pop();
      if (lastTag) {
        handleTagRemove(lastTag);
      }
    }
  };

  const getPlaceholderText = () => {
    if (disabled) {
      return t('coursesPage.searchDisabled');
    }

    const hasTags = includeTags.length > 0 || excludeTags.length > 0;
    return hasTags || searchText ? t('coursesPage.searchContinue') : placeholder;
  };

  const hasAnySearchParams = !!(searchText?.trim() || includeTags?.length || excludeTags?.length);

  return (
    <ClickAwayListener
      onClickAway={() => {
        setShowTagOptions(false);
        setShowSuggestions(false);
      }}
    >
      <SearchContainer>
        <SearchWrapper
          ref={containerRef}
          onClick={handleContainerClick}
          style={{ opacity: disabled ? 0.6 : 1 }}
        >
          {selectedTags.length > 0 && (
            <TagsContainer>
              {selectedTags.map(tag => {
                const tagData = availableTags.find(t => t.value === tag.name);
                const displayName = tagData ? getTagTranslation(tagData) : tag.name;

                return (
                  <TagAnimation key={tag.name}>
                    <SearchTag
                      name={tag.name}
                      type={tag.type}
                      displayName={displayName}
                      onToggle={() => handleTagToggle(tag.name, tag.type)}
                      onRemove={() => handleTagRemove(tag.name)}
                      disabled={disabled}
                    />
                  </TagAnimation>
                );
              })}
            </TagsContainer>
          )}

          {selectedTags.length > 0 && <Separator />}

          <SearchInputContainer>
            <SearchInput
              ref={inputRef}
              value={inputValue}
              onChange={handleSearchTextChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholderText()}
              disabled={disabled}
              hasError={false}
            />
          </SearchInputContainer>

          {hasAnySearchParams && !disabled && (
            <ClearButton
              onClick={handleClearAll}
              size="small"
              aria-label={t('coursesPage.clearSearch', 'Clear search')}
            >
              ✕
            </ClearButton>
          )}
        </SearchWrapper>

        {showSuggestions && suggestions.length > 0 && (
          <SuggestionOverlay>
            {suggestions.map((tag, index) => {
              const isInclude = searchText.startsWith('+');
              const tagType = isInclude ? TagTypeConst.Include : TagTypeConst.Exclude;

              return (
                <SuggestionItem
                  key={tag.value}
                  data-selected={index === selectedSuggestionIndex}
                  onClick={() => handleSuggestionSelect(tag, tagType)}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: isInclude ? Colors.green : Colors.red,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: Colors.white,
                      fontSize: '10px'
                    }}
                  >
                    {isInclude ? '+' : '-'}
                  </Box>
                  {getTagTranslation(tag)}
                </SuggestionItem>
              );
            })}
          </SuggestionOverlay>
        )}

        {ariaLiveMessage && (
          <Box
            component="output"
            sx={{
              position: 'absolute',
              left: '-10000px',
              width: '1px',
              height: '1px',
              overflow: 'hidden'
            }}
            aria-live="polite"
          >
            {ariaLiveMessage}
          </Box>
        )}

        <Popper
          open={showTagOptions && Boolean(anchorEl) && !disabled}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{
            zIndex: 1300,
            minWidth: anchorEl?.offsetWidth ?? 400,
            maxWidth: anchorEl?.offsetWidth ?? 800,
            position: 'absolute'
          }}
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                altBoundary: false,
                padding: 8,
                altAxis: false,
                rootBoundary: 'viewport'
              }
            },
            {
              name: 'flip',
              enabled: false
            },
            {
              name: 'offset',
              options: {
                offset: [0, 4]
              }
            }
          ]}
        >
          <TagOptions
            availableTags={availableTagsToShow}
            selectedTagsCount={selectedTags.length}
            maxTags={10}
            isLoading={areTagsLoading}
            onTagAdd={handleTagAdd}
            getTagTranslation={getTagTranslation}
          />
        </Popper>
      </SearchContainer>
    </ClickAwayListener>
  );
};
